import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import Cookies from "js-cookie";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import { TrendingUp, TrendingDown, Activity, DollarSign, Target } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const TrendsForecasting = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [activeTab, setActiveTab] = useState("historical");
  const [trendTab, setTrendTab] = useState("revenue");
  const [scenarioTab, setScenarioTab] = useState("whatif");
  const [months, setMonths] = useState(12);
  
  const [revenueTrends, setRevenueTrends] = useState(null);
  const [costTrends, setCostTrends] = useState(null);
  const [profitabilityTrends, setProfitabilityTrends] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [scenarioData, setScenarioData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan, months]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("enakid_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      const [revenueRes, costRes, profitRes, forecastRes, scenarioRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/actual-data/trends/revenue/${currentPlan.id}?months=${months}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/trends/cost/${currentPlan.id}?months=${months}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/trends/profitability/${currentPlan.id}?months=${months}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/forecast/adjustments/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/forecast/scenarios/${currentPlan.id}`, { headers })
      ]);
      
      setRevenueTrends(revenueRes.data);
      setCostTrends(costRes.data);
      setProfitabilityTrends(profitRes.data);
      setForecastData(forecastRes.data);
      setScenarioData(scenarioRes.data);
    } catch (err) {
      console.error("Error fetching trends & forecasting:", err);
      setError(err.response?.data?.detail || "Failed to load trends & forecasting");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading Trends & Forecasting...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <InfoTooltip content="Time-series analysis of historical performance to identify patterns, trends, and forecast future outcomes. Shows: historical data patterns (seasonality, growth trends), forecast adjustments, scenario analysis (best/worst/likely case). Key concepts: Trend = direction over time, Seasonality = recurring patterns, Forecast = projection based on history. Review monthly for trends, quarterly for forecasts, annually for strategic planning.">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">📈 Trends & Forecasting</h1>
          </InfoTooltip>
          <p className="text-slate-600">Analyze historical trends and forecast future performance</p>
        </div>

        {/* Month Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <InfoTooltip content="Choose analysis period. 12 Months = recent trends, 24 Months = strategic planning (recommended), 36 Months = long-term patterns. More months = stable trends but slower to react. Fewer months = responsive but higher volatility.">
                <span className="font-medium">Time Period:</span>
              </InfoTooltip>
              {[12, 24, 36].map((m) => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    months === m
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {m} Months
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-xl">
            <InfoTooltip content="Analyze past performance: trend lines (actual vs planned), seasonality patterns, growth rates, efficiency metrics. Use to understand what happened, identify patterns for forecasting, and validate if initiatives are working.">
              <TabsTrigger value="historical">Historical Trends</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Update forecasts based on actual performance trends. Compares: Original Plan, Actual Trends, Revised Forecast. Shows confidence levels and updated projections using actual growth rates. Use for mid-year reforecasting and dynamic budgeting.">
              <TabsTrigger value="forecast">Forecast Adjustments</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Explore different possible futures: Best Case, Likely Case, Worst Case scenarios. Includes sensitivity analysis showing which factors have biggest impact. Use for strategic planning, risk management, and showing range of outcomes to stakeholders. Always plan for 3 scenarios!">
              <TabsTrigger value="scenario">Scenario Analysis</TabsTrigger>
            </InfoTooltip>
          </TabsList>

          {/* Tab 1: Historical Trends */}
          <TabsContent value="historical" className="space-y-6">
            <Tabs value={trendTab} onValueChange={setTrendTab}>
              <TabsList className="grid grid-cols-3 w-full max-w-xl">
                <InfoTooltip content="Revenue performance: Actual vs Planned comparison, seasonality patterns, month-over-month growth rates. Answers: Is revenue growing/declining? Beating/missing plan? Seasonal patterns? Expected peaks/valleys?">
                  <TabsTrigger value="revenue">Revenue Trends</TabsTrigger>
                </InfoTooltip>
                <InfoTooltip content="Cost analysis: categories stacked over time, cost run rate, cost efficiency (cost-to-revenue ratio). Answers: Are costs controlled? Which categories growing fastest? Becoming more/less efficient?">
                  <TabsTrigger value="cost">Cost Trends</TabsTrigger>
                </InfoTooltip>
                <InfoTooltip content="Margin analysis: profit margin evolution (revenue - costs), unit economics trends. Answers: Are margins improving/eroding? Becoming more profitable? What's driving margin changes - revenue growth or cost control?">
                  <TabsTrigger value="profitability">Profitability Trends</TabsTrigger>
                </InfoTooltip>
              </TabsList>

              {/* Revenue Trends Sub-Tab */}
              <TabsContent value="revenue" className="space-y-6">
                {revenueTrends && (
                  <>
                    {/* Line Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Monthly actual (solid green line) vs planned revenue (dashed blue line). Read: green above blue = beating plan, below = missing plan. Line slope: upward = growth, flat = stagnant, downward = declining (urgent!). Use to identify growth inflection points and validate forecast accuracy.">
                          <CardTitle>📊 Revenue Trend ({months} Months)</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Actual vs Planned Revenue</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={revenueTrends.line_chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Actual Revenue" />
                            <Line type="monotone" dataKey="planned" stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 5" name="Planned Revenue" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Seasonality Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Recurring revenue patterns. Index 100 = average. Above 100 = peak season, below 100 = off season. High bars (e.g., 130) = 30% above average. Use for: resource planning (staff/inventory in peaks), cash flow planning, forecasting (adjust by seasonality index), performance evaluation.">
                          <CardTitle>📊 Seasonality Index</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Revenue patterns across periods (100 = average)</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={revenueTrends.seasonality_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis label={{ value: 'Seasonality Index', angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Bar dataKey="index" fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Growth Rate Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Revenue growth % month-to-month. Formula: (Current - Previous) / Previous × 100. Green bars = positive growth, Red = decline. Tall green (e.g., +20%) = strong growth. Red bars = investigate immediately. Use to track if hitting growth targets and measure initiative impact. Sustained >10% monthly = aggressive growth!">
                          <CardTitle>📊 Month-over-Month Growth Rate</CardTitle>
                        </InfoTooltip>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={revenueTrends.growth_rate_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis label={{ value: 'Growth Rate (%)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                            <Bar dataKey="growth_rate" fill="#10b981">
                              {revenueTrends.growth_rate_data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.growth_rate >= 0 ? '#10b981' : '#ef4444'} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>

              {/* Cost Trends Sub-Tab */}
              <TabsContent value="cost" className="space-y-6">
                {costTrends && (
                  <>
                    {/* Stacked Area Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Stacked area showing cost category contributions. Each colored layer = one category. Total height = total monthly costs. Layer thickness = category contribution. Use to identify: which categories drive costs, if categories growing at different rates, cost structure changes. Action if: costs growing faster than revenue (margin compression).">
                          <CardTitle>📊 Cost Categories Over Time</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Stacked area view of all cost categories</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <AreaChart data={costTrends.stacked_area_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {costTrends.categories.map((category, idx) => (
                              <Area
                                key={category}
                                type="monotone"
                                dataKey={category}
                                stackId="1"
                                stroke={COLORS[idx % COLORS.length]}
                                fill={COLORS[idx % COLORS.length]}
                              />
                            ))}
                          </AreaChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Run Rate Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Monthly actual costs (solid red) vs cumulative average (dashed blue). Formula: Run Rate = Total spending ÷ Months elapsed. Red above blue = current month higher than average (burning faster). Use for burn rate monitoring and budget pacing. Annualized run rate = Monthly run rate × 12.">
                          <CardTitle>📊 Cost Run Rate</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Monthly cost vs cumulative average</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={costTrends.run_rate_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={2} name="Monthly Cost" />
                            <Line type="monotone" dataKey="run_rate" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" name="Run Rate (Avg)" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Efficiency Metrics */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Cost-to-Revenue Ratio (%). Formula: (Total Costs / Total Revenue) × 100. Lower = better efficiency. Benchmarks: World-class 40-60%, Healthy 60-80%, Warning 80-95%, Critical >95%. Trend: Downward = improving efficiency, Upward = margin erosion (investigate!). Profit Margin = 100% - Ratio.">
                          <CardTitle>📊 Cost Efficiency (Cost to Revenue Ratio)</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Lower is better</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={costTrends.efficiency_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis label={{ value: 'Cost/Revenue (%)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                            <Line type="monotone" dataKey="cost_to_revenue_ratio" stroke="#f59e0b" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>

              {/* Profitability Trends Sub-Tab */}
              <TabsContent value="profitability" className="space-y-6">
                {profitabilityTrends && (
                  <>
                    {/* Margin Evolution */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Revenue (green), Cost (red), and Profit Margin % (blue) trends. Formula: Profit Margin % = (Revenue - Cost) / Revenue × 100. Patterns: Revenue growing faster than costs = margin improving (good leverage). Costs growing faster = margin eroding (investigate!). Use to show profitability trajectory to stakeholders.">
                          <CardTitle>📊 Margin Evolution</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Profit margin trend over time</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={profitabilityTrends.margin_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis yAxisId="left" tickFormatter={(value) => formatCurrency(value)} />
                            <YAxis yAxisId="right" orientation="right" label={{ value: 'Margin %', angle: 90, position: 'insideRight' }} />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" />
                            <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={2} name="Cost" />
                            <Line yAxisId="right" type="monotone" dataKey="margin_pct" stroke="#3b82f6" strokeWidth={3} name="Margin %" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Unit Economics Trend */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Revenue, cost, and profit per unit over time. Bars: Green (revenue/unit), Red (cost/unit), Blue (profit/unit). Formula: Profit/unit = Revenue/unit - Cost/unit. Benchmarks: Good >30% margin, Acceptable 15-30%, Warning 5-15%, Critical <5% or negative. Negative profit/unit = unsustainable! Fix before scaling.">
                          <CardTitle>📊 Unit Economics Trend</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Revenue, cost, and profit per unit</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={profitabilityTrends.unit_economics_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="revenue_per_unit" fill="#10b981" name="Revenue/Unit" />
                            <Bar dataKey="cost_per_unit" fill="#ef4444" name="Cost/Unit" />
                            <Bar dataKey="profit_per_unit" fill="#3b82f6" name="Profit/Unit" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Tab 2: Forecast Adjustments */}
          <TabsContent value="forecast" className="space-y-6">
            {forecastData && (
              <>
                {/* Dual Line Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Three-line comparison: Original Plan (dashed blue) = initial forecast, Actual (solid green) = historical performance, Revised Forecast (solid orange) = updated projection based on actual trends. Use for: expectation management, resource planning, mid-year reforecasting. Update quarterly for dynamic budgeting.">
                      <CardTitle>Original Plan vs Actuals-Based Forecast</CardTitle>
                    </InfoTooltip>
                    <CardDescription>
                      Comparing original projections with revised forecast based on actual trends
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={forecastData.chart_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip formatter={(value) => value ? formatCurrency(value) : "N/A"} />
                        <Legend />
                        <Line type="monotone" dataKey="original_plan" stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 5" name="Original Plan" />
                        <Line type="monotone" dataKey="actual" stroke="#10b981" strokeWidth={2} name="Actual" />
                        <Line type="monotone" dataKey="revised_forecast" stroke="#f59e0b" strokeWidth={3} name="Revised Forecast" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Average Growth Rate Card */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Average Growth Rate (Based on Actuals)</p>
                      <p className={`text-4xl font-bold ${forecastData.avg_growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {forecastData.avg_growth_rate >= 0 ? '+' : ''}{forecastData.avg_growth_rate.toFixed(2)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Forecast Revision Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Month-by-month comparison: Original Plan, Revised Forecast, Variance, Variance %. Positive variance (green) = revised higher than original. Negative variance (red) = revised lower. Large % variances (>20%) = major revision. Track quarterly to validate accuracy.">
                      <CardTitle>📋 Forecast Revision Table</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Period</th>
                            <th className="text-right p-3 font-semibold">Original Plan</th>
                            <th className="text-right p-3 font-semibold">Actual Trend</th>
                            <th className="text-right p-3 font-semibold">Revised Forecast</th>
                            <th className="text-center p-3 font-semibold">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {forecastData.revision_table.map((row, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3">{row.period}</td>
                              <td className="p-3 text-right">{formatCurrency(row.original_plan)}</td>
                              <td className="p-3 text-right">{formatCurrency(row.actual_trend)}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(row.revised_forecast)}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-1 rounded text-sm ${
                                  row.confidence === 'High' ? 'bg-green-100 text-green-800' :
                                  row.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {row.confidence}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Tab 3: Scenario Analysis */}
          <TabsContent value="scenario" className="space-y-6">
            <Tabs value={scenarioTab} onValueChange={setScenarioTab}>
              <TabsList className="grid grid-cols-2 w-full max-w-md">
                <InfoTooltip content="Build multiple scenarios (Best/Likely/Worst cases) to understand range of outcomes. Shows scenario comparison chart and detailed month-by-month tables. Use to test different assumptions, communicate uncertainty, and prepare contingency plans.">
                  <TabsTrigger value="whatif">What-If Scenarios</TabsTrigger>
                </InfoTooltip>
                <InfoTooltip content="Identify business factors with biggest profit impact. Shows: Tornado chart (factors ranked by impact width), Risk vs Return scatter plot (scenarios by risk/return). Use to prioritize management attention on highest sensitivity factors.">
                  <TabsTrigger value="sensitivity">Sensitivity Analysis</TabsTrigger>
                </InfoTooltip>
              </TabsList>

              {/* What-If Scenarios */}
              <TabsContent value="whatif" className="space-y-6">
                {scenarioData && (
                  <>
                    {/* Multi-line Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Three scenarios: Best Case (green, optimistic), Likely Case (blue, most probable), Worst Case (red, pessimistic). Wide gap between lines = high uncertainty. Narrow gap = predictable business. Use for: risk planning (worst case), opportunity sizing (best case), resource allocation (budget between likely and worst).">
                          <CardTitle>📊 Scenario Comparison (Best/Likely/Worst)</CardTitle>
                        </InfoTooltip>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={scenarioData.scenario_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="best_case" stroke="#10b981" strokeWidth={2} name="Best Case" />
                            <Line type="monotone" dataKey="likely" stroke="#3b82f6" strokeWidth={3} name="Likely" />
                            <Line type="monotone" dataKey="worst_case" stroke="#ef4444" strokeWidth={2} name="Worst Case" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Scenario Comparison Table */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Detailed month-by-month Revenue and Profit for each scenario. Use to: see specific projected numbers, plan cash flow by scenario, set monthly targets (use Likely for goals), communicate to CFO/board. Track actuals against scenarios to adjust strategy.">
                          <CardTitle>📋 Scenario Comparison</CardTitle>
                        </InfoTooltip>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left p-3 font-semibold">Scenario</th>
                                <th className="text-right p-3 font-semibold">12-Month Revenue</th>
                                <th className="text-right p-3 font-semibold">Growth Rate</th>
                                <th className="text-center p-3 font-semibold">Probability</th>
                              </tr>
                            </thead>
                            <tbody>
                              {scenarioData.comparison_table.map((row, idx) => (
                                <tr key={idx} className="border-b hover:bg-gray-50">
                                  <td className="p-3 font-medium">{row.scenario}</td>
                                  <td className="p-3 text-right">{formatCurrency(row.revenue_12m)}</td>
                                  <td className={`p-3 text-right ${row.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {row.growth_rate >= 0 ? '+' : ''}{row.growth_rate.toFixed(1)}%
                                  </td>
                                  <td className="p-3 text-center">{row.probability}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>

              {/* Sensitivity Analysis */}
              <TabsContent value="sensitivity" className="space-y-6">
                {scenarioData && (
                  <>
                    {/* Tornado Chart */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Horizontal bars showing profit impact by factor. Each row = one factor (pricing, volume, cost). Red (left) = low-end impact, Green (right) = high-end. Bar width = total swing. Widest bars at top = highest sensitivity factors (focus here!). Narrowest at bottom = low impact. Use for: prioritizing initiatives, risk management, scenario planning.">
                          <CardTitle>📊 Tornado Chart (Impact Ranking)</CardTitle>
                        </InfoTooltip>
                        <CardDescription>Factors ranked by impact on profitability</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={scenarioData.tornado_data} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                            <YAxis dataKey="factor" type="category" width={150} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="low_impact" fill="#ef4444" name="Low Impact" />
                            <Bar dataKey="high_impact" fill="#10b981" name="High Impact" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Risk vs Return Scatter */}
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Scatter plot: Risk (X-axis, uncertainty) vs Return (Y-axis, profitability %). Each dot = one scenario. Quadrants: Top-right (high return, high risk) = aggressive, Top-left (high return, low risk) = IDEAL, Bottom-right (low return, high risk) = AVOID, Bottom-left (low return, low risk) = conservative. Use for strategy selection and portfolio balancing.">
                          <CardTitle>📊 Risk vs Return Analysis</CardTitle>
                        </InfoTooltip>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <ScatterChart>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" dataKey="risk" name="Risk" label={{ value: 'Risk Level', position: 'insideBottom', offset: -5 }} />
                            <YAxis type="number" dataKey="return" name="Return" label={{ value: 'Return (%)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                            <Scatter name="Scenarios" data={scenarioData.scatter_data} fill="#3b82f6">
                              {scenarioData.scatter_data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Scatter>
                          </ScatterChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TrendsForecasting;
